// ============================================
// hooks/usePorchlight.js
// ============================================
import { useState, useEffect } from 'react';
import { subscribeToPorchlightStatus, isPorchlightActive } from '../models/porchlightModel';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { trackEvent, AnalyticsEvents } from '../services/analyticsService';
import { ERRORS } from '../utils/constants';

export const usePorchlight = (userId) => {
  const [porchlightUntil, setPorchlightUntil] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const isE2E = () => {
    try {
      if (typeof window === 'undefined') return false;
      if (window.Cypress) return true;
      // Fallback: our Cypress login sets this key
      return !!window.localStorage.getItem('e2eLoggedIn');
    } catch (_) {
      return false;
    }
  };
  // In Cypress, we normally stub network calls for speed/stability. Allow tests
  // to force real network (so cy.intercept can simulate errors) by setting
  // localStorage key 'E2E_FORCE_NETWORK' to 'true'.
  const isE2ENetworkStubbed = () => {
    try {
      if (typeof window === 'undefined') return false;
      if (!window.Cypress) return false;
      const forceReal = window.localStorage.getItem('E2E_FORCE_NETWORK') === 'true';
      return !forceReal;
    } catch (_) {
      return false;
    }
  };
  
  // Writes are intentionally performed via the callable Cloud Function
  // 'setPorchlightDuration' to ensure server timestamps, friend visibility,
  // and notifications are handled consistently on the backend.
  const setPorchlightDuration = httpsCallable(functions, 'setPorchlightDuration');

  useEffect(() => {
    if (!userId) return;

    const unsubscribe = subscribeToPorchlightStatus(userId, (status) => {
      setPorchlightUntil(status.until || null);
      setIsActive(isPorchlightActive(status.until));
    });

    return unsubscribe;
  }, [userId]);

  const updatePorchlight = async (minutes, selectedFriends = []) => {
    setLoading(true);
    try {
      // Cypress E2E (default): simulate success without calling backend
      // unless tests explicitly force real network via E2E_FORCE_NETWORK
      if (isE2ENetworkStubbed()) {
        const until = minutes > 0 ? new Date(Date.now() + minutes * 60 * 1000) : null;
        setPorchlightUntil(until);
        setIsActive(minutes > 0);
        trackEvent(AnalyticsEvents.PORCHLIGHT_TOGGLE, {
          state: minutes === 0 ? 'off' : 'on',
          minutes,
          selected_count: Array.isArray(selectedFriends) ? selectedFriends.length : 0,
        });
        return { success: true };
      }

      await setPorchlightDuration({ minutes, selectedFriends });
      // Analytics: porchlight toggled
      trackEvent(AnalyticsEvents.PORCHLIGHT_TOGGLE, {
        state: minutes === 0 ? 'off' : 'on',
        minutes,
        selected_count: Array.isArray(selectedFriends) ? selectedFriends.length : 0,
      });
      return { success: true };
    } catch (error) {
      const msg = String(error?.message || '');
      const code = String(error?.code || '');
      const status = Number(error?.status || error?.response?.status || 0);
      if (status === 429 || /resource-exhausted|quota|limit/i.test(msg) || /resource-exhausted|quota|limit/i.test(code)) {
        return { success: false, error: ERRORS.QUOTA_EXCEEDED };
      }
      if (status === 504 || /timeout|deadline/i.test(msg) || /deadline-exceeded/i.test(code)) {
        return { success: false, error: ERRORS.TIMEOUT };
      }
      return { success: false, error: ERRORS.GENERIC_ERROR };
    } finally {
      setLoading(false);
    }
  };

  return {
    porchlightUntil,
    isActive,
    loading,
    updatePorchlight
  };
};

